import React from "react";
import { SearchResultsProps } from "../../types/searchResults";
import ProductsGrid from "./ProductsGrid";
import CategoriesBlock from "./CategoriesBlock";
import SearchHistoryBlock from "./SearchHistoryBlock";

/**
 * SearchResults - Main container for search results UI
 *
 * Implements the layout according to the design mockup:
 * - Top: Search bar (provided by parent) + quick query chips
 * - Left column: Categories and suggestions
 * - Right zone: Products grid
 * - Bottom: "All products" button
 *
 * Handles all UI states: loading, error, empty, success.
 */
const SearchResults: React.FC<SearchResultsProps> = ({ hits, normalizedResults, query, history, onHistorySelect, isLoading, hasError, isEmpty, error, onRetry, loadMore, hasMore = false, className = "" }) => {
	// Use normalized products if available, otherwise fall back to raw hits
	const products = normalizedResults?.products || hits;
	const visibleProducts = products;

	const showLoadMoreButton = hasMore && !isLoading && !hasError && !isEmpty;

	const handleLoadMore = () => {
		if (loadMore) {
			loadMore();
		}
	};

	// Handle retry button click
	const handleRetry = () => {
		if (onRetry) {
			onRetry();
		} else {
			console.log("Retry search requested");
		}
	};

	// Default suggestion click handler
	const defaultSuggestionClick = (suggestion: string) => {
		console.log("Suggestion clicked:", suggestion);
	};

	// Show error state
	if (hasError) {
		return (
			<div className={`search-results error ${className}`} style={styles.errorContainer}>
				<div style={styles.errorContent}>
					<i className="fas fa-exclamation-triangle" style={styles.errorIcon}></i>
					<h3 style={styles.errorTitle}>Ошибка поиска</h3>
					<p style={styles.errorMessage}>{error || "Произошла ошибка при поиске"}</p>
					<button onClick={handleRetry} style={styles.retryButton}>
						<i className="fas fa-redo" style={styles.retryIcon}></i>
						Попробовать снова
					</button>
				</div>
			</div>
		);
	}

	// Show empty state
	if (isEmpty && !isLoading) {
		return (
			<div className={`search-results empty ${className}`} style={styles.emptyContainer}>
				<div style={styles.emptyContent}>
					<i className="fas fa-search" style={styles.emptyIcon}></i>
					<h3 style={styles.emptyTitle}>Ничего не найдено по запросу "{query}"</h3>
					<p style={styles.emptyMessage}>Попробуйте другие ключевые слова или проверьте правильность написания</p>
					<div style={styles.suggestions}>
						<p style={styles.suggestionsTitle}>Попробуйте поискать:</p>
						<div style={styles.suggestionChips}>
							{["dress", "electronics", "home", "sports"].map((term) => (
								<button key={term} onClick={() => defaultSuggestionClick(term)} style={styles.suggestionChip}>
									{term}
								</button>
							))}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show loading or success state with full layout
	return (
		<div className={`search-results ${className}`} style={styles.container}>
			{/* History matches */}
			{history && onHistorySelect && <SearchHistoryBlock history={history} currentQuery={query} onSelect={onHistorySelect} />}

			{/* Categories */}
			{normalizedResults?.categories && <CategoriesBlock categories={normalizedResults.categories} onCategorySelect={(category) => console.log("Category selected:", category.title, category.id)} />}

			{/* Results block */}
			<div>
				<ProductsGrid products={visibleProducts} isLoading={isLoading} hasMore={hasMore} />

				{/* Show more button for pagination */}
				{showLoadMoreButton && (
					<div style={styles.footer}>
						<button onClick={handleLoadMore} style={styles.loadMoreButton}>
							Показать еще
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

// Minimal inline styles - using flex/grid layouts
const styles = {
	container: {
		display: "flex",
		flexDirection: "column" as const,
		gap: "8px",
	},
	errorContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "400px",
		backgroundColor: "#fef2f2",
		borderRadius: "8px",
		padding: "32px",
	},
	errorContent: {
		textAlign: "center" as const,
		maxWidth: "400px",
	},
	errorIcon: {
		fontSize: "48px",
		color: "#ef4444",
		marginBottom: "16px",
	},
	errorTitle: {
		margin: "0 0 8px 0",
		fontSize: "18px",
		fontWeight: 600,
		color: "#334155",
	},
	errorMessage: {
		margin: "0 0 16px 0",
		fontSize: "14px",
		color: "#64748b",
	},
	retryButton: {
		padding: "10px 20px",
		backgroundColor: "#2563eb",
		color: "#ffffff",
		border: "none",
		borderRadius: "4px",
		cursor: "pointer",
		fontSize: "14px",
		display: "inline-flex",
		alignItems: "center",
		gap: "8px",
	},
	retryIcon: {
		fontSize: "12px",
	},
	emptyContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "400px",
		backgroundColor: "#f8fafc",
		borderRadius: "8px",
		padding: "32px",
	},
	emptyContent: {
		textAlign: "center" as const,
		maxWidth: "400px",
	},
	emptyIcon: {
		fontSize: "48px",
		color: "#cbd5e1",
		marginBottom: "16px",
	},
	emptyTitle: {
		margin: "0 0 8px 0",
		fontSize: "18px",
		fontWeight: 600,
		color: "#334155",
	},
	emptyMessage: {
		margin: "0 0 24px 0",
		fontSize: "14px",
		color: "#64748b",
	},
	suggestions: {
		marginTop: "24px",
	},
	suggestionsTitle: {
		margin: "0 0 12px 0",
		fontSize: "14px",
		color: "#64748b",
	},
	suggestionChips: {
		display: "flex",
		flexWrap: "wrap" as const,
		gap: "8px",
		justifyContent: "center",
	},
	suggestionChip: {
		padding: "6px 12px",
		backgroundColor: "#ffffff",
		border: "1px solid #e2e8f0",
		borderRadius: "16px",
		fontSize: "12px",
		color: "#475569",
		cursor: "pointer",
	},
	mainContent: {
		display: "grid",
		gridTemplateColumns: "250px 1fr",
		gap: "24px",
	},
	leftColumn: {
		display: "flex",
		flexDirection: "column" as const,
		gap: "16px",
	},
	rightColumn: {
		display: "flex",
		flexDirection: "column" as const,
		gap: "16px",
	},
	resultsBlock: {
		backgroundColor: "#ffffff",
		border: "1px solid #e2e8f0",
		borderRadius: "8px",
		padding: "16px",
		marginBottom: "16px",
	},
	sectionTitle: {
		margin: "0 0 16px 0",
		fontSize: "16px",
		fontWeight: 600,
		color: "#334155",
	},
	resultsHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		paddingBottom: "12px",
		borderBottom: "1px solid #e2e8f0",
	},
	resultsTitle: {
		margin: 0,
		fontSize: "18px",
		fontWeight: 600,
		color: "#334155",
	},
	resultsCount: {
		fontSize: "14px",
		color: "#64748b",
	},
	footer: {
		display: "flex",
		justifyContent: "center",
		paddingTop: "16px",
	},
	loadMoreButton: {
		padding: "12px 24px",
		color: "#fff",
		backgroundColor: "#454647",
		borderRadius: "8px",
		fontSize: "18px",
		fontWeight: 400,
		cursor: "pointer",
		display: "flex",
		width: "100%",
		justifyContent: "center" as const,
		alignItems: "center",
		gap: "8px",
		transition: "all 0.2s ease",
	},
	allProductsIcon: {
		margin: "0px",
	},
};

export default SearchResults;
